export const defaultProfile = {
  role: '',
  careerLevels: [],
  seniority: '',
  sector: '',
  managementExperience: '',
  primaryObjective: '',
  currentChallenge: '',
  desiredOutcome: '',
  preferredEmphasis: '',
  learningStyle: '',
  urgencyLevel: '',
  priorExecutiveEducation: '',
  formatPreference: '',
  completionWindow: '',
  travelFlexibility: '',
  goals: '',
  certificationPath: '',
  alreadyTakenCourses: []
};

export const PATHWAY_DRAFT_KEY = 'execEye.pathwayDraft';

export function readPathwayDraft() {
  try {
    return JSON.parse(window.localStorage.getItem(PATHWAY_DRAFT_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

export const roleSuggestions = [
  'Board Member',
  'C-Level Executive',
  'Chair of Board',
  'Entrepreneur',
  'General Manager',
  'Director',
  'Head of Function',
  'Manager/ Team Lead',
  'Consultant',
  'Specialist',
  'Young Professional'
];

export const sectorSuggestions = [
  'Healthcare',
  'Financial Services',
  'Technology',
  'Manufacturing',
  'Public Sector',
  'Energy',
  'Retail',
  'Professional Services'
];

export const objectiveSuggestionPriority = {
  'broader-leadership': {
    roles: ['General Manager', 'Director', 'Head of Function', 'Manager/ Team Lead'],
    sectors: ['Professional Services', 'Public Sector', 'Manufacturing', 'Retail']
  },
  'lead-change': {
    roles: ['Head of Function', 'Director', 'General Manager', 'Manager/ Team Lead'],
    sectors: ['Healthcare', 'Public Sector', 'Manufacturing', 'Technology']
  },
  'strategic-growth': {
    roles: ['C-Level Executive', 'General Manager', 'Director', 'Board Member'],
    sectors: ['Financial Services', 'Technology', 'Professional Services', 'Retail']
  },
  'finance-fluency': {
    roles: ['Director', 'General Manager', 'C-Level Executive', 'Consultant'],
    sectors: ['Financial Services', 'Energy', 'Manufacturing', 'Healthcare']
  },
  'digital-transformation': {
    roles: ['Head of Function', 'Director', 'General Manager', 'Entrepreneur'],
    sectors: ['Technology', 'Financial Services', 'Healthcare', 'Retail']
  }
};

export const objectivePresets = {
  'broader-leadership': {
    preferredEmphasis: 'balanced',
    formatPreference: 'blended',
    completionWindow: '24-months',
    travelFlexibility: 'london-ok',
    certificationPath: 'CIM'
  },
  'lead-change': {
    preferredEmphasis: 'leadership',
    formatPreference: 'blended',
    completionWindow: '12-months',
    travelFlexibility: 'london-ok',
    certificationPath: 'CIM'
  },
  'strategic-growth': {
    preferredEmphasis: 'strategy',
    formatPreference: 'in-person',
    completionWindow: '12-months',
    travelFlexibility: 'london-ok',
    certificationPath: 'CIM'
  },
  'finance-fluency': {
    preferredEmphasis: 'finance',
    formatPreference: 'in-person',
    completionWindow: '6-months',
    travelFlexibility: 'london-ok',
    certificationPath: 'CIF'
  },
  'digital-transformation': {
    preferredEmphasis: 'digital',
    formatPreference: 'blended',
    completionWindow: '12-months',
    travelFlexibility: 'limited-travel',
    certificationPath: 'CIM'
  }
};

export const readinessFields = [
  {
    key: 'role',
    label: 'Career level selected',
    isComplete: (profile) => Array.isArray(profile.careerLevels)
      ? profile.careerLevels.length > 0
      : Boolean((profile.role || '').trim()),
    hint: 'Select or type a career level for better programme fit.'
  },
  {
    key: 'sector',
    label: 'Sector context added',
    isComplete: (profile) => Boolean((profile.sector || '').trim()),
    hint: 'Industry context improves relevance for digital and transformation choices.'
  },
  {
    key: 'managementExperience',
    label: 'Management experience added',
    isComplete: (profile) => Boolean(profile.managementExperience),
    hint: 'Years of leadership experience calibrate pathway level.'
  },
  {
    key: 'primaryObjective',
    label: 'Primary objective chosen',
    isComplete: (profile) => Boolean(profile.primaryObjective),
    hint: 'Choose the main development outcome before comparing pathways.'
  },
  {
    key: 'currentChallenge',
    label: 'Current blocker identified',
    isComplete: (profile) => Boolean(profile.currentChallenge),
    hint: 'Add the immediate blocker to improve practical programme fit.'
  },
  {
    key: 'deliveryConstraints',
    label: 'Format preference set',
    isComplete: (profile) => Boolean(profile.formatPreference),
    hint: 'Delivery preference keeps recommendations practical.'
  },
  {
    key: 'goals',
    label: 'Context narrative added',
    isComplete: (profile) => Boolean((profile.goals || '').trim()),
    hint: 'A short sentence on goals or constraints improves programme reasoning.'
  }
];

export const valueLabels = {
  seniority: {
    Entry: 'Entry level',
    Mid: 'Mid-level',
    Senior: 'Senior',
    Executive: 'Executive'
  },
  managementExperience: {
    '0-3': '0-3 years',
    '4-7': '4-7 years',
    '8-12': '8-12 years',
    '12+': '12+ years'
  },
  primaryObjective: {
    'broader-leadership': 'Broader general management capability',
    'lead-change': 'Lead change and organisational transformation',
    'strategic-growth': 'Sharpen strategy and growth decisions',
    'finance-fluency': 'Build finance confidence',
    'digital-transformation': 'Lead digital transformation'
  },
  currentChallenge: {
    'cross-functional-alignment': 'Cross-functional alignment and execution drift',
    'decision-velocity': 'Slow strategic decision velocity',
    'change-adoption': 'Low change adoption and engagement',
    'commercial-acumen': 'Commercial and financial confidence gap',
    'digital-prioritization': 'Digital prioritization and ROI uncertainty',
    other: 'Other challenge'
  },
  desiredOutcome: {
    'step-into-gm': 'Prepare to step into General Management',
    'lead-transformation-programmes': 'Lead enterprise transformation programmes',
    'improve-strategic-finance': 'Strengthen strategic finance and board fluency',
    'scale-digital-execution': 'Scale digital execution across functions',
    'build-leadership-bench': 'Build leadership bench strength for the team'
  },
  preferredEmphasis: {
    balanced: 'Balanced pathway',
    leadership: 'Leadership-heavy',
    strategy: 'Strategy-heavy',
    finance: 'Finance-heavy',
    digital: 'Digital/transformation-heavy'
  },
  learningStyle: {
    'case-driven': 'Case-driven and discussion-heavy',
    'toolkit-first': 'Practical toolkit and frameworks first',
    'coaching-reflective': 'Coaching and reflective learning',
    cohort: 'Peer cohort learning'
  },
  urgencyLevel: {
    immediate: 'Immediate (next 3 months)',
    this_year: 'This year (3-12 months)',
    exploratory: 'Exploratory (12+ months)'
  },
  priorExecutiveEducation: {
    none: 'No prior executive education',
    'some-exposure': 'Some prior executive education',
    extensive: 'Extensive prior executive education'
  },
  formatPreference: {
    'in-person': 'Mostly in-person',
    blended: 'Blended learning',
    online: 'Mostly online',
    'no-preference': 'No strong preference'
  },
  completionWindow: {
    '6-months': 'Within 6 months',
    '12-months': 'Within 12 months',
    '24-months': 'Within full CIM/CIF window',
    flexible: 'Flexible timing'
  },
  travelFlexibility: {
    'london-ok': 'Comfortable travelling to London',
    'limited-travel': 'Limited travel capacity',
    'online-first': 'Online-first if possible'
  }
};

export function labelValue(field, value) {
  if (!value) return '';
  const map = valueLabels[field];
  return map && map[value] ? map[value] : value;
}
